import { useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connectorApi } from '../api';

const FLOW = {
  IDLE: 'IDLE',
  PROVIDER_SELECT: 'PROVIDER_SELECT',
  VDSC_REGISTER_MODE: 'VDSC_REGISTER_MODE',
  LINK_FORM: 'LINK_FORM',
  LINK_OTP: 'LINK_OTP',
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  REGISTER_FORM: 'REGISTER_FORM',
  VDSC_EKYC_OCR: 'VDSC_EKYC_OCR',
  VDSC_EKYC_REVIEW: 'VDSC_EKYC_REVIEW',
  VDSC_MANUAL_FORM: 'VDSC_MANUAL_FORM',
  VDSC_REGISTER_OTP: 'VDSC_REGISTER_OTP',
  SUCCESS: 'SUCCESS',
};

const STEP_TO_FLOW = {
  undefined: FLOW.PROVIDER_SELECT,
  link: FLOW.LINK_FORM,
  'link-otp': FLOW.LINK_OTP,
  register: FLOW.REGISTER_FORM,
  success: FLOW.SUCCESS,
  'not-found': FLOW.ACCOUNT_NOT_FOUND,
  'register-mode': FLOW.VDSC_REGISTER_MODE,
  'ekyc-ocr': FLOW.VDSC_EKYC_OCR,
  'ekyc-review': FLOW.VDSC_EKYC_REVIEW,
  'manual-form': FLOW.VDSC_MANUAL_FORM,
  'register-otp': FLOW.VDSC_REGISTER_OTP,
};

const FLOW_TO_STEP = {
  [FLOW.PROVIDER_SELECT]: '',
  [FLOW.LINK_FORM]: '/link',
  [FLOW.LINK_OTP]: '/link-otp',
  [FLOW.REGISTER_FORM]: '/register',
  [FLOW.SUCCESS]: '/success',
  [FLOW.ACCOUNT_NOT_FOUND]: '/not-found',
  [FLOW.VDSC_REGISTER_MODE]: '/register-mode',
  [FLOW.VDSC_EKYC_OCR]: '/ekyc-ocr',
  [FLOW.VDSC_EKYC_REVIEW]: '/ekyc-review',
  [FLOW.VDSC_MANUAL_FORM]: '/manual-form',
  [FLOW.VDSC_REGISTER_OTP]: '/register-otp',
};

const BASE_PATH = '/xchange/connector';

const formatError = (err) => {
  const data = err.data;
  if (data?.message && data?.code) {
    return { code: data.code, message: data.message };
  }
  return { code: null, message: err.message || 'Đã xảy ra lỗi' };
};

export default function useConnector() {
  const { providerCode, step } = useParams();
  const navigate = useNavigate();

  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [initResult, setInitResult] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [ekycSession, setEkycSession] = useState(null);
  const loadedRef = useRef(false);

  const flowState = useMemo(() => {
    if (!providerCode) return FLOW.IDLE;
    return STEP_TO_FLOW[step] || FLOW.PROVIDER_SELECT;
  }, [providerCode, step]);

  const selectedProvider = useMemo(() => {
    if (!providerCode) return null;
    return providers.find((p) => p.code === providerCode) || { code: providerCode };
  }, [providerCode, providers]);

  const activeConnections = connections.filter((c) => c.status !== 'DISCONNECTED');

  const navigateToFlow = useCallback((flow, pc) => {
    const code = pc || providerCode;
    const suffix = FLOW_TO_STEP[flow];
    if (suffix != null && code) {
      navigate(`${BASE_PATH}/${code}${suffix}`, { replace: true });
    } else {
      navigate(BASE_PATH, { replace: true });
    }
  }, [navigate, providerCode]);

  const loadData = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoadingData(true);
    try {
      const [providersRes, connectionsRes] = await Promise.all([
        connectorApi.getProviders(),
        connectorApi.getConnections(),
      ]);
      setProviders(providersRes.data || []);
      setConnections(connectionsRes.data || []);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoadingData(false);
    }
  }, []);

  const refreshConnections = useCallback(async () => {
    try {
      const res = await connectorApi.getConnections();
      setConnections(res.data || []);
    } catch {
    }
  }, []);

  const selectProvider = useCallback((provider) => {
    setError(null);
    navigate(`${BASE_PATH}/${provider.code}`);
  }, [navigate]);

  const loadCatalogs = useCallback(async (pc) => {
    if (catalogs) return;
    const code = pc || providerCode;
    if (!code) return;
    const [provinces, banks, nationalities, issuePlaces, phonePostals] = await Promise.all([
      connectorApi.getCatalog('PROVINCE', code).then((r) => r.data || []),
      connectorApi.getCatalog('BANK', code).then((r) => r.data || []),
      connectorApi.getCatalog('NATIONALITY', code).then((r) => r.data || []),
      connectorApi.getCatalog('ISSUE_PLACE', code).then((r) => r.data || []),
      connectorApi.getCatalog('PHONE_POSTAL', code).then((r) => r.data || []),
    ]);
    setCatalogs({ provinces, banks, nationalities, issuePlaces, phonePostals });
  }, [providerCode, catalogs]);

  const startLinkFlow = useCallback(async () => {
    if (!providerCode) return;
    setError(null);
    setLoading(true);
    try {
      await loadCatalogs();
      navigate(`${BASE_PATH}/${providerCode}/link`);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [providerCode, loadCatalogs, navigate]);

  const startRegisterFlow = useCallback(async () => {
    if (!providerCode) return;
    setError(null);
    setLoading(true);
    try {
      await loadCatalogs();
      navigate(`${BASE_PATH}/${providerCode}/register`);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [providerCode, loadCatalogs, navigate]);

  const submitLink = useCallback(async (linkBody) => {
    if (!providerCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.linkConnection(providerCode, linkBody);
      const data = res.data;
      setInitResult(data);
      if (data.status === 'ACCOUNT_NOT_FOUND') {
        navigate(`${BASE_PATH}/${providerCode}/not-found`, { replace: true });
        return data;
      }
      if (data.otpRequired) {
        navigate(`${BASE_PATH}/${providerCode}/link-otp`, { replace: true });
      } else {
        navigate(`${BASE_PATH}/${providerCode}/success`, { replace: true });
        await refreshConnections();
      }
      return data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [providerCode, refreshConnections, navigate]);

  const submitOtp = useCallback(async (sessionToken, otp) => {
    if (!providerCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.confirmConnection(providerCode, {
        sessionToken,
        verificationToken: otp,
      });
      navigate(`${BASE_PATH}/${providerCode}/success`, { replace: true });
      await refreshConnections();
      return res.data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [providerCode, refreshConnections, navigate]);

  const submitRegister = useCallback(async (regData) => {
    if (!providerCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.registerConnection(providerCode, regData);
      setInitResult(res.data);
      navigate(`${BASE_PATH}/${providerCode}/success`, { replace: true });
      await refreshConnections();
      return res.data;
    } catch (err) {
      setError(formatError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [providerCode, refreshConnections, navigate]);

  const startVdscRegister = useCallback(() => {
    if (!providerCode) return;
    setError(null);
    navigate(`${BASE_PATH}/${providerCode}/register-mode`, { replace: true });
  }, [providerCode, navigate]);

  const initVdscRegister = useCallback(async (inputType) => {
    if (!providerCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.vdscInitEkyc(inputType);
      const session = res.data;
      setEkycSession({ sessionToken: session.sessionToken, inputType });
      if (inputType === 'MANUAL') {
        await loadCatalogs();
        navigate(`${BASE_PATH}/${providerCode}/manual-form`, { replace: true });
      } else {
        navigate(`${BASE_PATH}/${providerCode}/ekyc-ocr`, { replace: true });
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [providerCode, loadCatalogs, navigate]);

  const submitVdscOcr = useCallback(async (frontCard, backCard) => {
    if (!ekycSession?.sessionToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.vdscSubmitOcr(ekycSession.sessionToken, frontCard, backCard);
      const data = res.data;
      setEkycSession((prev) => ({
        ...prev,
        sessionToken: data.sessionToken || prev.sessionToken,
        ocrData: data.extraData || {},
      }));
      navigate(`${BASE_PATH}/${providerCode}/ekyc-review`, { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [ekycSession, providerCode, navigate]);

  const submitVdscRegister = useCallback(async (formData) => {
    if (!providerCode || !ekycSession?.sessionToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.registerConnection(providerCode, {
        sessionToken: ekycSession.sessionToken,
        extraData: formData,
      });
      const data = res.data;
      setEkycSession((prev) => ({
        ...prev,
        sessionToken: data.sessionToken || prev.sessionToken,
      }));
      setInitResult(data);
      navigate(`${BASE_PATH}/${providerCode}/register-otp`, { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [providerCode, ekycSession, navigate]);

  const submitVdscOtp = useCallback(async (otp) => {
    if (!providerCode || !ekycSession?.sessionToken) return;
    setLoading(true);
    setError(null);
    try {
      await connectorApi.confirmConnection(providerCode, {
        sessionToken: ekycSession.sessionToken,
        verificationToken: otp,
      });
      navigate(`${BASE_PATH}/${providerCode}/success`, { replace: true });
      await refreshConnections();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [providerCode, ekycSession, refreshConnections, navigate]);

  const disconnect = useCallback(async (pc) => {
    setLoading(true);
    setError(null);
    try {
      await connectorApi.disconnect(pc);
      await refreshConnections();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [refreshConnections]);

  const viewDetail = useCallback(async (pc) => {
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.getConnectionDetail(pc);
      return res.data;
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const syncStatus = useCallback(async (pc) => {
    setLoading(true);
    setError(null);
    try {
      const res = await connectorApi.syncStatus(pc);
      await refreshConnections();
      return res.data;
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [refreshConnections]);

  const goBack = useCallback(() => {
    setError(null);
    setInitResult(null);
    if (flowState === FLOW.VDSC_EKYC_REVIEW) {
      navigate(`${BASE_PATH}/${providerCode}/ekyc-ocr`, { replace: true });
    } else if (flowState === FLOW.VDSC_REGISTER_OTP) {
      const prevStep = ekycSession?.inputType === 'MANUAL' ? 'manual-form' : 'ekyc-review';
      navigate(`${BASE_PATH}/${providerCode}/${prevStep}`, { replace: true });
    } else if (flowState === FLOW.VDSC_EKYC_OCR || flowState === FLOW.VDSC_MANUAL_FORM) {
      setEkycSession(null);
      navigate(`${BASE_PATH}/${providerCode}/register-mode`, { replace: true });
    } else if (flowState === FLOW.VDSC_REGISTER_MODE) {
      navigate(`${BASE_PATH}/${providerCode}`, { replace: true });
    } else if (flowState === FLOW.PROVIDER_SELECT) {
      setEkycSession(null);
      navigate(BASE_PATH);
    } else if (flowState === FLOW.ACCOUNT_NOT_FOUND) {
      navigate(`${BASE_PATH}/${providerCode}`, { replace: true });
    } else {
      setEkycSession(null);
      navigate(`${BASE_PATH}/${providerCode}`, { replace: true });
    }
  }, [flowState, providerCode, ekycSession, navigate]);

  const goHome = useCallback(() => {
    setError(null);
    setInitResult(null);
    setEkycSession(null);
    navigate(BASE_PATH);
  }, [navigate]);

  const clearError = useCallback(() => setError(null), []);

  return {
    providers,
    connections,
    activeConnections,
    selectedProvider,
    flowState,
    initResult,
    catalogs,
    ekycSession,
    loading,
    loadingData,
    error,
    loadData,
    loadCatalogs,
    selectProvider,
    startLinkFlow,
    startRegisterFlow,
    submitLink,
    submitOtp,
    submitRegister,
    startVdscRegister,
    initVdscRegister,
    submitVdscOcr,
    submitVdscRegister,
    submitVdscOtp,
    disconnect,
    viewDetail,
    syncStatus,
    goBack,
    goHome,
    clearError,
    FLOW,
  };
}
